<!-- SubPage.vue -->
<template>
  <div id="wrap">
    
    <!--[웹접근성] 바로가기 메뉴-->
    <ul id="skip_nav">
      <li><a href="#header"><h2>주메뉴 바로가기</h2></a></li>
      <li><a href="#container"><h2>본문 바로가기</h2></a></li>
      <li><a href="#footer"><h2>하단 바로가기</h2></a></li>
    </ul>
    <!--// [웹접근성] 바로가기 메뉴-->

    <Header
        :menus="topMenus"
        :selected-menu-id="selectedMenuId"
        :is-logged-in="isLoggedIn"
        @menu-change="handleMenuChange"
        @login="isLoggedIn = true"
        @logout="isLoggedIn = false"
    />
    <!-- 모바일 메뉴 -->
    <MobileMenu
      :menus="allMenusTree"
      :selected-menu-id="selectedMenuId"
      :is-logged-in="isLoggedIn"
      @menu-change="handleMenuChange"
      @logout="isLoggedIn = false"
    />
    <div id="container">
      <SubHeader
        :title="currentTitle"
        :breadcrumbs="currentBreadcrumbs"
      />
      <SubNav
        :menus="sideMenus"
        :current-menu="subNavCurrentMenu"
        :title="selectedTopMenuName"
      />
      <!-- Content -->
      <div id="content" class="cont_wrap">
        <header class="cont_head">
					<!-- 컨텐츠 제목 -->
					<div class="area_title">
						<h1 class="title">
              <span>{{ displayTitle }}</span>
						</h1>  
          </div>
				</header>
        <!--
          메뉴 컨텐츠상단/하단 (MENU_CONTS_T / MENU_CONTS_B)
          - 관리자 메뉴관리에서 입력한 HTML이 렌더링되는 영역
          - 모든 메뉴 유형(BBS, INPRG, CONTENT, LINK 등)에 공통 적용
          - 레거시: content.css(약 2,900줄) + common.css 유틸리티 클래스 의존
            (.fc01~09 글자색, .bc01~09 배경색, .tal/.tac/.tar 정렬, .fwb 볼드,
             .subTitle, .tab_wrap, .process01~04, .img_conLst 등)
          - 이관 데이터가 레거시 클래스를 사용할 경우 별도 CSS 포함 필요
          - 신규 작성 시 인라인 스타일 권장
        -->
        <!-- 메뉴 컨텐츠상단 -->
        <div v-if="currentNode?.menuContsT" v-dompurify-html="currentNode.menuContsT" class="menu-conts-top"></div>

        <div class="cont_body">
          <!-- tabGroup.jsp: Lvl3MenuGroups (항상 tmn01) -->
          <TabGroup
            v-if="tabLvl3MenuGroups.length > 0"
            :items="tabLvl3MenuGroups"
            nav-type="T"
            :active-id="tabActiveId"
            @menu-change="handleMenuChange"
          />
          <!-- tabGroup.jsp: upperMenuGroups (상위 메뉴 형제들, navType 있을 때만) -->
          <TabGroup
            v-if="tabUpperNavType"
            :items="tabUpperMenuGroups"
            :nav-type="tabUpperNavType"
            :active-id="tabUpperActiveId"
            @menu-change="handleMenuChange"
          />
          <!-- tabGroup.jsp: menuGroups (자신의 메뉴 형제들, navType 있을 때만) -->
          <TabGroup
            v-if="tabMenuNavType"
            :items="tabMenuGroups"
            :nav-type="tabMenuNavType"
            :active-id="tabActiveId"
            @menu-change="handleMenuChange"
          />
          <slot/>
        </div>

        <!-- 메뉴 컨텐츠하단 -->
        <div v-if="currentNode?.menuContsB" v-dompurify-html="currentNode.menuContsB" class="menu-conts-bottom"></div>

        <div class="cont_foot">
          <SubFooter
            :menu-id="menuId"
            :manager-tel="managerTel"
            :formatted-mod-date="formattedModDate"
          />
        </div>
      </div>
    </div>
    <FloatingMenu/>
    <Footer/>
  </div>
</template>

<script setup>
import {computed, ref, onMounted, watch} from 'vue'
import {FloatingMenu, Footer, Header, MobileMenu, SubFooter, SubHeader, SubNav, TabGroup} from '@/components/layout/index.js'
import {useApiMenu} from '@/composables/useApiMenu.js'
import {useCurrentMenu} from '@/composables/useCurrentMenu.js'
import {provideMenuContext} from '@/composables/useMenuContext.js'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/store/user.store'

const route = useRoute()
const userStore = useUserStore()

defineOptions({name: 'DefaultLayout'})

provideMenuContext()

defineProps({
  initialLoggedIn: {type: Boolean, default: false},
})

// userStore에서 로그인 상태 가져오기
const isLoggedIn = computed(() => userStore.isLoggedIn)

const DOMAIN_ID = import.meta.env.VITE_DOMAIN_ID
const selectedMenuId = ref(null)

const {
  getMenuTree,
  getSubTreeByParent,
  getMenuBreadcrumb,
  getChildMenus,
  findMenuById
} = useApiMenu()

const { currentMenu, currentTitle, currentBreadcrumbs } = useCurrentMenu()

const APP_NAME = '대구통합예약시스템'

const topMenus = computed(() => {
  return getMenuTree(DOMAIN_ID)
})

const allMenusTree = computed(() => {
  return getMenuTree(DOMAIN_ID)
})

const selectedTopMenu = computed(() => {
  return topMenus.value.find(m => m.menuId === selectedMenuId.value) || null
})

const selectedTopMenuName = computed(() => {
  return selectedTopMenu.value?.menuNm || ''
})

// 제목 가공: '전체'일 경우 '1차메뉴명 전체'로 변경
const getProcessedTitle = () => {
  const t = (currentTitle.value || '').trim()
  if (t === '전체' && selectedTopMenuName.value) {
    return `${selectedTopMenuName.value} 전체`
  }
  return t
}

watch(
  () => [route.fullPath, currentTitle.value, selectedTopMenuName.value],
  () => {
    const t = getProcessedTitle()
    document.title = t ? `${t} | ${APP_NAME}` : APP_NAME
  },
  { immediate: true }
)

const sideMenus = computed(() => {
  if (!selectedMenuId.value) return []
  // 현재 메뉴가 menuIndctYn='N'이어도 표시되도록 currentMenu.menuId 전달
  const currentMenuId = currentMenu.value?.menuId
  const tree = getSubTreeByParent(selectedMenuId.value, currentMenuId)
  return tree
})

// SubNav에 전달할 활성 메뉴:
// TabGroup 등으로 3차+ 메뉴 이동 시 SubNav(2차 목록)에서 부모가 활성화되도록
// breadcrumb 중 selectedTopMenu(1차) 바로 아래 자식(2차)을 찾아 전달
const subNavCurrentMenu = computed(() => {
  const menu = currentMenu.value
  if (!menu) return null
  const topId = selectedMenuId.value
  if (!topId) return menu

  const crumb = getMenuBreadcrumb(menu.menuId) || []
  // breadcrumb에서 1차(topId) 바로 다음 메뉴가 SubNav에서 활성화될 2차 메뉴
  const topIndex = crumb.findIndex(m => m.menuId === topId)
  if (topIndex !== -1 && topIndex + 1 < crumb.length) {
    return crumb[topIndex + 1]
  }
  return menu
})

const handleMenuChange = (menuId) => {
  // 2차/3차 메뉴 ID가 넘어오더라도 항상 1차(루트) 메뉴 ID로 정규화
  // — Header에서 2차 메뉴를 클릭하면 child.menuId가 emit되기 때문
  const breadcrumb = getMenuBreadcrumb(menuId) || []
  const rootMenu = breadcrumb.length > 0 ? breadcrumb[0] : null
  selectedMenuId.value = rootMenu?.menuId || menuId
}

// 화면 제목: 현재 메뉴명이 있으면 그것을 우선 사용, '전체'일 경우 '1차메뉴명 전체'로 변경
const displayTitle = computed(() => {
  const title = (currentTitle.value || selectedTopMenuName.value || '').trim()
  if (title === '전체' && selectedTopMenuName.value) {
    return `${selectedTopMenuName.value} 전체`
  }
  return title
})

// 현재 활성 메뉴 정보(메타 포함) 확보
const currentNode = computed(() => {
  const menu = currentMenu.value
  if (!menu) return null
  return findMenuById(menu.menuId) || menu
})

/* 카멜/스네이크 키 모두 대응해서 안전하게 필드 빼기 */
function pick(node, camel, snake) {
  return node?.[camel] ?? node?.[snake] ?? null
}

const menuId = computed(() => currentNode.value?.menuId || '')

/* 전화번호 */
const managerTel = computed(() => pick(currentNode.value, 'managerTel'))

/* 최근자료수정일 (YYYY-MM-DD 또는 YYYYMMDD를 YYYY-MM-DD로 표준화) */
const modDateRaw = computed(() =>
  pick(currentNode.value, 'modDate', 'mod_date') ||
  pick(currentNode.value, 'modYmd', 'mod_ymd')
)

const formattedModDate = computed(() => {
  const v = modDateRaw.value
  if (!v) return ''
  const s = String(v)
  if (s.includes('-')) return s.slice(0, 10)
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  return s
})

// -------------------- TabGroup 데이터 --------------------
// breadcrumb 기준으로 형제/상위 메뉴 그룹 산출
const currentBreadcrumb = computed(() => {
  if (!currentMenu.value?.menuId) return []
  return getMenuBreadcrumb(currentMenu.value.menuId) || []
})

const sortedChildren = (parentMenuId) => {
  if (!parentMenuId) return []
  return getChildMenus(parentMenuId).sort((a, b) => (a.sortSeq || 0) - (b.sortSeq || 0))
}

// 자신의 메뉴그룹: 현재 메뉴의 형제들 (부모의 자식들)
const tabMenuGroups = computed(() => {
  const crumb = currentBreadcrumb.value
  if (crumb.length < 2) return []
  return sortedChildren(crumb[crumb.length - 2]?.menuId)
})

// 상위 메뉴그룹: 부모의 형제들 (조부모의 자식들)
const tabUpperMenuGroups = computed(() => {
  const crumb = currentBreadcrumb.value
  if (crumb.length < 3) return []
  return sortedChildren(crumb[crumb.length - 3]?.menuId)
})

// Lvl3 메뉴그룹: breadcrumb 중 level 3에 해당하는 메뉴의 형제들
const tabLvl3MenuGroups = computed(() => {
  const crumb = currentBreadcrumb.value
  const lvl3Index = crumb.findIndex(m => m.menuLevel === 3)
  if (lvl3Index < 1) return []
  return sortedChildren(crumb[lvl3Index - 1]?.menuId)
})


const VALID_NAV_TYPES = ['T', 'Y', 'E']

// ?debug-tabs=T|Y|E 쿼리로 강제 navType 지정 (개발 테스트용)
const debugNavType = computed(() => {
  const v = String(route.query['debug-tabs'] || '').toUpperCase()
  return VALID_NAV_TYPES.includes(v) ? v : null
})

const getNavType = (groups) => {
  if (debugNavType.value) return debugNavType.value
  const t = groups[0]?.navType
  return VALID_NAV_TYPES.includes(t) ? t : null
}

const tabActiveId = computed(() => currentMenu.value?.menuId || null)
const tabUpperActiveId = computed(() => currentMenu.value?.upMenuId || null)

// navType이 유효한 경우에만 표시
const tabMenuNavType = computed(() => getNavType(tabMenuGroups.value))
const tabUpperNavType = computed(() => getNavType(tabUpperMenuGroups.value))

const updateSelectedTopMenuByCurrentMenu = () => {
  const menu = currentMenu.value
  if (menu) {
    const path = getMenuBreadcrumb(menu.menuId) || []
    const root = path.length > 0 ? path[0] : menu
    
    if (root.menuId) {
      selectedMenuId.value = root.menuId
    }
  } else {
    selectedMenuId.value = null
  }
}

onMounted(() => {
  updateSelectedTopMenuByCurrentMenu()
})

watch(
  () => currentMenu.value,
  () => {
    updateSelectedTopMenuByCurrentMenu()
  }
)

// body에 data-menu-idtop 속성 설정 (CSS 선택자용)
watch(
  () => selectedMenuId.value,
  (newMenuId) => {
    if (newMenuId) {
      document.body.setAttribute('data-menu-idtop', String(newMenuId))
    } else {
      document.body.removeAttribute('data-menu-idtop')
    }
  }
)

// body에 data-menu-type / data-menu-idup 속성 설정 (모바일 전체메뉴 현재 메뉴 활성화용)
watch(
  () => currentMenu.value,
  (menu) => {
    const menuType = String(menu?.menuId || '')
    const menuIdUp = String(menu?.upMenuId || '')

    if (menuType) {
      document.body.setAttribute('data-menu-type', menuType)
    } else {
      document.body.removeAttribute('data-menu-type')
    }

    if (menuIdUp) {
      document.body.setAttribute('data-menu-idup', menuIdUp)
    } else {
      document.body.removeAttribute('data-menu-idup')
    }
  },
  { immediate: true }
)
</script>
